import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { captureException } from '@/lib/errors'
import { useMutation } from '@/lib/mutations'
import { useStore } from '@/lib/pocketbase'

export function usePendingInvites() {
    const params = useLocalSearchParams<{ orgSlug: string }>()

    const [invitationsCollection, orgsCollection] = useStore('invitations', 'orgs')

    const { data = [], isLoading } = useLiveQuery(
        q =>
            params.orgSlug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, params.orgSlug))
                      .join({ invitation: invitationsCollection }, ({ org, invitation }) =>
                          eq(invitation.org, org.id)
                      )
                      .where(({ invitation }) => eq(invitation?.used ?? true, false))
                      .select(({ invitation }) => ({ invitation }))
                : undefined,
        [params.orgSlug]
    )

    const invites = data.map(d => d.invitation).filter((i): i is NonNullable<typeof i> => !!i)

    const revokeMutation = useMutation({
        mutationFn: function* (inviteId: string) {
            yield invitationsCollection.delete(inviteId)
        },
        onError: error => {
            captureException('Revoke invitation', error)
        },
    })

    return {
        invites,
        isLoading,
        revokeInvite: revokeMutation.mutateAsync,
        isRevoking: revokeMutation.isPending,
    }
}
